import type { FC } from 'react';
import type { BookBaseColor } from 'shared/types/Branding';

import { useMemo, useState } from 'react';
import { AddressElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { LinearProgress } from '@mui/material';
import styled from 'styled-components';

import { filterBackgroundColor } from 'shared/helpers/website';
import useErrorLogger from 'shared/hooks/useErrorLogger';
import tinycolor from 'tinycolor2';
import { COLOR_BLACK, COLOR_WHITE } from 'shared/helpers/colors';
import { ZOOM_SIZE } from 'shared/helpers/static';
import { generateAccessKey } from 'shared/helpers/utils';

export type FirstInputType = {
  phone?: string;
  email: string;
  first_name: string;
  last_name: string;
  subscribe: boolean;
  city: string;
  street: string;
  apartment: string;
  zipcode: string;
  state: string;
};

export type SecondInputTypes = {
  backgroundColor: string;
  callback: (args: unknown) => void;
  textColor: BookBaseColor;
  orderData: FirstInputType;
  oppositeBackgroundColor?: string | undefined;
  order: number;
};

const Button = styled.button<{ backgroundColor: string; textColor: string; editable: boolean }>`
  min-height: 10px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  padding: 24px 40px;
  border-radius: 40px;
  text-align: center;
  transition:
    transform 0.2s ease 0s,
    box-shadow 0.2s ease 0s;
  cursor: pointer;
  background-color: ${(props) => props.backgroundColor};
  border: none;

  ${(props) =>
    props.editable
      ? ''
      : `
  &:hover {
    transform: translateY(-3px);
    box-shadow: rgba(0, 0, 0, 0.2) 0px 1rem 2rem;
  }`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 17px 28px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    &:hover {
      transform: translateY(0px);
      box-shadow: rgba(0, 0, 0, 0) 0px 0rem 0rem;
    }
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 100%;
    padding: 25px 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  `}

  ${({ theme: { $width } }) =>
    $width <= 375 &&
    `
    padding: 18px 10px;
  `}

  & p {
    ${(props) => `color: ${props.textColor}`}
  }
`;

const Container = styled.div`
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 30px;
`;

const Form = styled.form`
  width: 100%;
`;

const LoadingWrapper = styled.div`
  margin-top: 25px;
  height: 100%;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 45px;
  display: flex;
  align-items: center;
  justify-content: end;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    justify-content: center;
  `}
`;

const ZoomWrapper = styled.div`
  width: 100%;
  zoom: ${1 + (1 - ZOOM_SIZE + 0.1)};
  margin-bottom: 20px;
`;

const Title = styled.h3`
  margin-bottom: 20px;
  font-weight: 600;
  line-height: 1.3;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const Second: FC<SecondInputTypes> = (props) => {
  const { backgroundColor, callback, textColor, orderData, oppositeBackgroundColor, order } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const { stripeErrorHandler, logError } = useErrorLogger();

  // Accessibility improvements
  const formId = `payment-form-${order || 'checkout'}`;
  const submitAccessKey = useMemo(() => generateAccessKey('p', order), [order]);
  const titleId = `payment-title-${order || 'checkout'}`;

  const handlePaymentError = (error: Error | unknown, isStripeError = false) => {
    const errorMessage = error instanceof Error ? error.message : 'Payment processing failed';

    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'assertive');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Payment error: ${errorMessage}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 5000);

    if (!isStripeError) {
      logError(error);
    }
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      handlePaymentError(
        new Error('Payment system not ready. Please refresh the page and try again.')
      );
      return;
    }

    try {
      setLoading(true);

      // Announce payment processing to screen readers
      const processingAnnouncement = document.createElement('div');
      processingAnnouncement.setAttribute('aria-live', 'polite');
      processingAnnouncement.setAttribute('aria-atomic', 'true');
      processingAnnouncement.className = 'sr-only';
      processingAnnouncement.textContent = 'Processing payment. Please wait...';
      document.body.appendChild(processingAnnouncement);

      const response = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${process.env['APP_BASE_URL']}`
        },
        redirect: 'if_required'
      });

      // Clean up processing announcement
      document.body.removeChild(processingAnnouncement);

      if (response.error) {
        handlePaymentError(response.error, true);
        stripeErrorHandler(response.error);
      } else {
        // Announce success to screen readers
        const successAnnouncement = document.createElement('div');
        successAnnouncement.setAttribute('aria-live', 'polite');
        successAnnouncement.setAttribute('aria-atomic', 'true');
        successAnnouncement.className = 'sr-only';
        successAnnouncement.textContent = 'Payment completed successfully!';
        document.body.appendChild(successAnnouncement);
        setTimeout(() => document.body.removeChild(successAnnouncement), 3000);

        callback({});
      }
    } catch (error) {
      handlePaymentError(error);
    } finally {
      setLoading(false);
    }
  };

  const color = useMemo(() => {
    return tinycolor(backgroundColor).isDark() ? COLOR_WHITE : COLOR_BLACK;
  }, [backgroundColor]);

  const finalTextColor = useMemo(
    () => filterBackgroundColor(oppositeBackgroundColor || '#fff', textColor),
    [oppositeBackgroundColor, textColor]
  );

  const isFormReady = stripe && elements && !loading;
  const customerName = `${orderData.first_name || ''} ${orderData.last_name || ''}`.trim();

  return (
    <Container role="region" aria-labelledby={titleId}>
      <Title id={titleId} style={{ color: finalTextColor }}>
        It's almost yours!
      </Title>

      {/* Screen reader instructions */}
      <VisuallyHidden>
        Complete your payment using the secure form below. Fill in your billing address and payment
        method details.
      </VisuallyHidden>

      <Form
        onSubmit={submitForm}
        id={formId}
        role="form"
        aria-label="Payment checkout form"
        noValidate>
        <fieldset disabled={loading} style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend className="sr-only">Payment Information</legend>

          <ZoomWrapper role="group" aria-label="Billing address">
            <AddressElement
              options={{
                mode: 'billing',
                display: {
                  name: 'full'
                },
                defaultValues: {
                  name: customerName,
                  address: {
                    line1: orderData.street || '',
                    line2: orderData.apartment || '',
                    city: orderData.city || '',
                    state: orderData.state || '',
                    postal_code: orderData.zipcode || '',
                    country: 'US'
                  }
                }
              }}
            />
          </ZoomWrapper>

          <ZoomWrapper role="group" aria-label="Payment method">
            <PaymentElement
              options={{
                defaultValues: {
                  billingDetails: {
                    name: customerName,
                    address: {
                      line1: orderData.street || '',
                      line2: orderData.apartment || '',
                      city: orderData.city || '',
                      state: orderData.state || '',
                      postal_code: orderData.zipcode || '',
                      country: 'US'
                    }
                  }
                }
              }}
            />
          </ZoomWrapper>
        </fieldset>

        {loading ? (
          <LoadingWrapper role="status" aria-label="Processing payment" aria-live="polite">
            <VisuallyHidden>Processing payment, please wait...</VisuallyHidden>
            <LinearProgress color="inherit" aria-hidden="true" />
          </LoadingWrapper>
        ) : (
          <ButtonWrapper>
            <Button
              editable={false}
              textColor={color}
              type="submit"
              backgroundColor={backgroundColor}
              disabled={!isFormReady || loading}
              dangerouslySetInnerHTML={{
                __html: '<p>COMPLETE PAYMENT >></p>'
              }}
              title={`Press Alt+${submitAccessKey} to complete payment`}
              aria-label="Complete payment and finish checkout"
              aria-describedby={loading ? 'payment-processing' : undefined}
            />

            {/* Hidden status for screen readers */}
            {loading && (
              <VisuallyHidden id="payment-processing">
                Payment is being processed. Please do not refresh the page or navigate away.
              </VisuallyHidden>
            )}
          </ButtonWrapper>
        )}
      </Form>

      {/* Security notice for screen readers */}
      <VisuallyHidden>
        This is a secure payment form. Your payment information is encrypted and protected.
      </VisuallyHidden>
    </Container>
  );
};
